import type { FC } from 'react';
import type { Card, PreviewCardProps, PreviewData, PreviewDataLoader, PreviewType } from './types';

interface TypedCardBuilder<T extends PreviewType> {
    withTitle: (title: string) => TitledCardBuilder<T>;
}

interface TitledCardBuilder<T extends PreviewType> {
    withData: (data: PreviewData<T>) => CompletedCardBuilder;
    withLoader: (loader: PreviewDataLoader<T>) => CompletedCardBuilder;
    withChildComponent: (child: FC) => CompletedCardBuilder;
}

interface CompletedCardBuilder {
    build: () => Card;
}

export class CardBuilder<T extends PreviewType>
    implements TypedCardBuilder<T>, TitledCardBuilder<T>, CompletedCardBuilder
{
    static ofType<T extends PreviewType>(type: T): TypedCardBuilder<T> {
        return new CardBuilder(type);
    }

    private title = '';
    private type: T;

    private data?: PreviewData<T>;
    private loader?: PreviewDataLoader<T>;
    private child?: FC;

    private constructor(type: T) {
        this.type = type;
    }

    withTitle(title: string): TitledCardBuilder<T> {
        this.title = title;

        return this;
    }

    withData(data: PreviewData<T>): CompletedCardBuilder {
        this.data = data;

        return this;
    }

    withLoader(loader: PreviewDataLoader<T>): CompletedCardBuilder {
        this.loader = loader;

        return this;
    }
    withChildComponent(child: FC): CompletedCardBuilder {
        this.child = child;

        return this;
    }

    build(): Card {
        const { title, type, data, loader, child } = this;

        let card: PreviewCardProps<T> | undefined;

        if (data) {
            card = {
                title,
                type,
                loader: async () => data,
            };
        } else if (loader) {
            card = {
                title,
                type,
                loader,
            };
        } else if (child) {
            card = {
                title,
                type,
                loader: async () => ({ childComponent: child }) as PreviewData<T>,
            };
        }

        if (!card) {
            throw new Error('Card build error: no data/loader/child provided');
        }

        return card as Card;
    }
}
